import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { isEmpty } from 'lodash';
import turf from '@turf/turf';

import { Box, Button, Grid, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import Videos from '../../../api/Videos/Videos';
import * as videoCalculate from '../../../api/Videos/videos-calculations';

import Annotations from '../../../api/Annotations/Annotations';
import Loading from '../Loading';
import useTimeStore from '../ZustandProvider';

const AnnotationDirectButtonsComponent = ({
  annotationList,
  isMobile,
  MapProps,
  loading,
  video
}) => {
  if (loading === true) {
    return <Loading />;
  }
  const { videoId, savingSystem, intervalTime } = MapProps.map.settingsAdvancedMap.directButtons;
  const coordOnMap = useTimeStore(state => state.coordOnMap);
  const drawerWidth = useTimeStore(state => state.drawerWidth);
  const geometryConfig = useTimeStore(state => state.geometryConfig);
  const setGeometryConfig = useTimeStore(state => state.setGeometryConfig);

  const fechaAGpsEnMicrosegundos = () => {
    const fechaConHora = new Date();
    const fechaReferenciaGps = new Date('1980-01-06T00:00:00Z');
    const diferencia = fechaConHora.getTime() - fechaReferenciaGps.getTime();
    const diferenciaEnMicroSegundos = diferencia * 1000;
    return diferenciaEnMicroSegundos;
  };

  const filterTemplates = !isEmpty(video) && video.annotationTemplates;

  const obtenerUbicacion = async () => {
    if ('geolocation' in navigator) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            const latitud = position.coords.latitude;
            const longitud = position.coords.longitude;
            const coordenadas = [longitud, latitud];
            resolve(coordenadas);
          },
          error => {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                reject(new Error('El usuario denegó la solicitud de geolocalización.'));
                break;
              case error.POSITION_UNAVAILABLE:
                reject(new Error('La información de la ubicación no está disponible.'));
                break;
              case error.TIMEOUT:
                reject(new Error('Se ha agotado el tiempo de espera para obtener la ubicación.'));
                break;
              case error.UNKNOWN_ERROR:
                reject(new Error('Se produjo un error desconocido al obtener la ubicación.'));
                break;
              default:
                reject(new Error('Se produjo un error desconocido al obtener la ubicación.'));
                break;
            }
          }
        );
      });
    }
    return Promise.reject(new Error('La geolocalización no está soportada por este navegador.'));
  };

  const videoTimeFunction = currentTimeGPS => {
    let time;
    if (savingSystem === 'incremental') {
      const numerosVideotime = !isEmpty(annotationList)
        ? annotationList.map(objeto => objeto.geoJSON.properties.videoTime)
        : [0];
      time = Math.max(...numerosVideotime) + parseInt(intervalTime, 10) * 1000000;
    } else if (savingSystem === 'location') {
      const newPointTurf = turf.point([coordOnMap.lng, coordOnMap.lat]);
      const newPointVideo = turf.nearestPointOnLine(video.track, newPointTurf);
      time = videoCalculate.calculateTimeVideoLine(video, newPointVideo, annotationList);
    } else if (savingSystem === 'gpsTime') {
      const numerosTimeGPS = !isEmpty(annotationList)
        ? annotationList.map(objeto => objeto.timeGPS)
        : [0];
      const initialTimeGPS = Math.min(...numerosTimeGPS);
      time = initialTimeGPS !== 0 ? currentTimeGPS - initialTimeGPS : currentTimeGPS;
    } else {
      time += 5 * 1000000;
    }
    return time;
  };

  const createAnnotation = async item => {
    try {
      const currentTimeGPS = fechaAGpsEnMicrosegundos();
      const coordenadas =
        geometryConfig === 'directButton'
          ? [coordOnMap.lng, coordOnMap.lat]
          : await obtenerUbicacion();

      const videoTimeValue = videoTimeFunction(currentTimeGPS);

      try {
        if (isEmpty(item)) throw new Error('No annotation template selected.');
        const emptyElements = item.elements.map(element => {
          const newElement = element;
          if ('caption' in element) {
            newElement.caption = '';
          }
          if ('labelName' in element) {
            newElement.labelName = '';
          }
          if ('optionList' in element) {
            newElement.optionList = [];
          }
          if ('value' in element) {
            newElement.value = '';
          }
          return newElement;
        });

        const annotationToSave = {
          geoJSON: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coordenadas
            },
            properties: {
              template: {
                _id: item._id,
                name: item.name,
                icon: item.icon,
                tags: [],
                elements: emptyElements,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt
              },
              video: videoId,
              videoTime: parseInt(videoTimeValue, 10),
              videoPosition: {
                x: 0,
                y: 0,
                width: 0,
                height: 0
              }
            }
          },
          origin: 'manual',
          owner: MapProps.map.owner
        };
        await methodPromise('annotations.insert', annotationToSave);
        Bert.alert('Annotation created', 'success');
      } catch (error) {
        handleErrorInClient(error);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <>
      <Grid container spacing={isMobile ? 2 : 4}>
        {filterTemplates.map(item => (
          <Grid key={item._id} item xs={isMobile ? 6 : 4}>
            <Tooltip title={item.name} placement="bottom" arrow>
              <Button
                variant="contained"
                size="large"
                style={{
                  borderRadius: 0,
                  padding: 0,
                  backgroundColor: '#2A3F90',
                  width:
                    isMobile === true
                      ? `${0.4 * window.innerWidth}px`
                      : `${(0.3 * drawerWidth * window.innerWidth) / 100}px`,
                  height:
                    isMobile === true
                      ? `${0.4 * window.innerWidth}px`
                      : `${(0.3 * drawerWidth * window.innerWidth) / 100}px`,
                  maxWidth: isMobile !== true && `${(0.3 * 50 * window.innerWidth) / 100}px`,
                  maxHeight: isMobile !== true && `${(0.3 * 50 * window.innerWidth) / 100}px`
                }}
                onClick={() => {
                  createAnnotation(item);
                }}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  style={{ width: '80%', height: '80%', color: 'white' }}
                />
              </Button>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
      <Box display="row" justifyContent="space-evenly" fullWidth mb={2}>
        <Button
          variant="outlined"
          size="large"
          style={{
            color: geometryConfig === 'directButton' ? 'white' : '#2A3F90',
            backgroundColor: geometryConfig === 'directButton' ? '#2A3F90' : 'white',
            borderColor: '#2A3F90',
            width: isMobile ? '100%' : 'calc(33.33% - 8px)', // Ajusta el ancho según la pantalla
            margin: isMobile ? '8px 0' : '0' // Añade margen en móvil para separar los botones
          }}
          onClick={() => {
            if (geometryConfig === 'directButton') {
              setGeometryConfig('none');
            } else {
              setGeometryConfig('directButton');
            }
          }}
        >
          Seleccionar punto en el mapa
        </Button>
      </Box>
    </>
  );
};

export default withTracker(({ MapProps }) => {
  const { videoId } = MapProps.map.settingsAdvancedMap.directButtons;
  const videoSub = Meteor.isClient && Meteor.subscribe('videos.view', videoId);
  const annotationSub = Meteor.subscribe('annotations', videoId);
  const video = Videos.findOne(videoId);

  return {
    loading: !annotationSub.ready() && !videoSub.ready(),
    annotationList: Annotations.find({}, { sort: { videoTime: 1 } }).fetch(),
    video
  };
})(AnnotationDirectButtonsComponent);
AnnotationDirectButtonsComponent.defaultProps = {
  annotationList: [],
  isMobile: false,
  loading: false,
  video: {}
};
AnnotationDirectButtonsComponent.propTypes = {
  annotationList: PropTypes.array,
  isMobile: PropTypes.bool,
  MapProps: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  video: PropTypes.object
};
